import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
    Truck,
    MapPin,
    Calendar,
    FileText,
    DollarSign,
    ClipboardList,
    ChevronDown,
    ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PickupPaymentModal from '../components/PickupPaymentModal';

import socketService from '../services/socketService';

const RoutePage = () => {
    const { token } = useAuth();
    const [routes, setRoutes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedRoutes, setExpandedRoutes] = useState({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Handler for PickupPaymentModal onConfirm
    const confirmPickupPayment = async ({ orderId, payments }) => {
        const fd = new FormData();
        fd.append('orderId', orderId);

        const paymentsMeta = payments.map((p, idx) => ({
            method: p.method,
            amount: p.amount,
            linkedMovementIds: p.linkedMovementIds,
            photoIndices: p.files.map((_, fIdx) => `${idx}_${fIdx}`)
        }));
        fd.append('payments', JSON.stringify(paymentsMeta));
        fd.append('notes', 'Recaudo de ruta - Recaudos de Ruta');

        payments.forEach((p) => {
            p.files.forEach((file) => {
                fd.append('photos', file);
            });
        });

        try {
            const res = await fetch('/api/logistics/receive-pickup-payment', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error registrando pago');
            toast.success(data.message || 'Pagos registrados correctamente');
            setShowPaymentModal(false);
            setSelectedOrder(null);
            fetchRoutes();
        } catch (err) {
            console.error('Error registrando pago:', err);
            toast.error(err.message || 'Error al registrar el pago');
            throw err;
        }
    };

    useEffect(() => {
        fetchRoutes();

        // Connect to socket
        socketService.connect(token);
        socketService.joinRoom('orders-updates');

        // Listen for updates
        socketService.socket.on('order-updated', handleOrderUpdate);

        return () => {
            socketService.socket.off('order-updated', handleOrderUpdate);
            socketService.leaveRoom('orders-updates');
        };
    }, [token]);

    const handleOrderUpdate = (data) => {
        const { order } = data;
        setRoutes(prevRoutes => {
            return prevRoutes.map(group => {
                const orderIndex = group.orders.findIndex(o => o.id === order.id);
                if (orderIndex === -1) return group;

                const updatedOrders = [...group.orders];
                const oldOrder = updatedOrders[orderIndex];

                const newPaidAmount = Number(order.paid_amount || 0);
                const totalValue = Number(oldOrder.total_value || 0);
                const newBalanceDue = Math.max(0, totalValue - newPaidAmount);

                updatedOrders[orderIndex] = {
                    ...oldOrder,
                    paid_amount: newPaidAmount,
                    balance_due: newBalanceDue,
                    payment_status: order.payment_status,
                    siigo_payment_info: order.siigo_payment_info
                };

                const newOrdersTotal = updatedOrders.reduce((sum, o) => sum + Number(o.balance_due || 0), 0);
                const stopsTotal = group.additionalStops.reduce((sum, s) => sum + Number(s.payment_amount || 0), 0);

                return {
                    ...group,
                    orders: updatedOrders,
                    totalExpected: newOrdersTotal + stopsTotal
                };
            });
        });
    };

    const fetchRoutes = async () => {
        try {
            setIsLoading(true);
            const res = await fetch('/api/cartera/route', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const data = await res.json();
            if (data.success) {
                setRoutes(data.data);
                setExpandedRoutes({});
            } else {
                toast.error('Error cargando rutas: ' + data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Error de conexión');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleRoute = (routeKey) => {
        setExpandedRoutes(prev => ({
            ...prev,
            [routeKey]: !prev[routeKey]
        }));
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-CO');
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Truck className="h-8 w-8 text-primary-600" />
                    Recaudos de Ruta
                </h1>
                <p className="text-gray-500 mt-1">
                    Monitor de pedidos activos por mensajero para recaudo.
                </p>
            </div>

            {routes.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg">No hay rutas activas en este momento.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {routes.map((group) => {
                        const routeKey = group.routeId ? `route_${group.routeId}` : `no_route_${group.messengerId}`;
                        const allDelivered = group.orders.every(o => o.messenger_status === 'delivered');
                        return (
                            <div key={routeKey} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                                {/* Header Ruta */}
                                <div
                                    className={`px-4 py-2 flex items-center justify-between cursor-pointer transition-all ${allDelivered ? 'bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100' : 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200'}`}
                                    onClick={() => toggleRoute(routeKey)}
                                >
                                    <div className="flex items-center gap-4">
                                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                            {expandedRoutes[routeKey] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                        </button>
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {group.routeName || 'Sin Ruta'}
                                                <span className="text-sm font-normal text-gray-500 ml-2">— {group.messenger}</span>
                                            </h3>
                                            <span className="text-[10px] text-gray-600">
                                                {group.orders.length} pedidos
                                                {allDelivered && <span className="ml-1 text-green-700 font-semibold">• Completada</span>}
                                                {group.closedAt && <span className="ml-1 text-gray-500">• Cerrada: {(() => { const d = new Date(group.closedAt); d.setHours(d.getHours() - 5); return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); })()}</span>}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-medium text-gray-600">Total Esperado</p>
                                        <p className={`text-lg font-bold ${group.totalExpected > 0 ? 'text-primary-600' : 'text-green-600'}`}>{formatCurrency(group.totalExpected)}</p>
                                    </div>
                                </div>

                                {/* Tabla de Pedidos */}
                                {expandedRoutes[routeKey] && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs text-left">
                                            <thead className="bg-gray-100 text-gray-600 border-b">
                                                <tr>
                                                    <th className="px-4 py-1.5 font-medium">Estado</th>
                                                    <th className="px-4 py-1.5 font-medium">F. Factura</th>
                                                    <th className="px-4 py-1.5 font-medium">Orden / Cliente</th>
                                                    <th className="px-4 py-1.5 font-medium">Información de Pago</th>
                                                    <th className="px-4 py-1.5 font-medium text-right">Total</th>
                                                    <th className="px-4 py-1.5 font-medium text-right">Pagado</th>
                                                    <th className="px-4 py-1.5 font-medium text-right">RCs (Recibos)</th>
                                                    <th className="px-4 py-1.5 font-medium text-right">Por Cobrar</th>
                                                    <th className="px-4 py-1.5 font-medium text-center">Gestión</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {/* Filas de Pedidos */}
                                                {group.orders.map((order) => {
                                                    // Parse RCs
                                                    let rcs = [];
                                                    try {
                                                        if (order.siigo_payment_info) {
                                                            rcs = typeof order.siigo_payment_info === 'string'
                                                                ? JSON.parse(order.siigo_payment_info)
                                                                : order.siigo_payment_info;
                                                        }
                                                    } catch (e) {
                                                        console.warn('Error parsing RCs', e);
                                                    }

                                                    return (
                                                        <tr key={order.id} className="hover:bg-gray-50">
                                                            <td className="px-4 py-2 align-top">
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${order.messenger_status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                                                                    order.messenger_status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                                                                        order.messenger_status === 'in_delivery' ? 'bg-purple-100 text-purple-800' :
                                                                            order.messenger_status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                                                'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {order.messenger_status === 'in_delivery' ? 'En Reparto' :
                                                                        order.messenger_status === 'accepted' ? 'Aceptado' :
                                                                            order.messenger_status === 'assigned' ? 'Asignado' :
                                                                                order.messenger_status === 'delivered' ? 'Entregado' :
                                                                                    order.messenger_status}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-[10px] text-gray-500">
                                                                {order.invoice_date ? (() => {
                                                                    const d = new Date(order.invoice_date);
                                                                    d.setHours(d.getHours() - 5);
                                                                    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
                                                                })() : '-'}
                                                            </td>
                                                            <td className="px-4 py-2 align-top">
                                                                <div className="font-bold text-gray-900">{order.order_number}</div>
                                                                <div className="font-medium text-gray-900 truncate max-w-[150px]">{order.customer_name}</div>
                                                                <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(order.created_at)}</div>
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-gray-600">
                                                                <div className="flex flex-col gap-1">
                                                                    <div className={`text-[10px] font-medium px-1.5 py-0.5 rounded w-fit ${order.payment_method === 'efectivo' ? 'bg-green-50 text-green-700 border border-green-200' :
                                                                        order.payment_method === 'transferencia' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                                            'bg-gray-50 text-gray-700 border border-gray-200'
                                                                        }`}>
                                                                        {order.payment_method?.toUpperCase()}
                                                                    </div>
                                                                    {order.internal_invoice_numbers && (
                                                                        <span className="text-[10px] text-purple-600 font-medium">
                                                                            INT: {order.internal_invoice_numbers}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-right font-medium text-gray-500">
                                                                <div className="flex flex-col items-end">
                                                                    <span>{formatCurrency(order.total_value)}</span>
                                                                    {Number(order.do_value) > 0 && (
                                                                        <span className="text-[10px] text-green-600">
                                                                            (DO {formatCurrency(order.do_value)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-right font-medium text-green-600">
                                                                {Number(order.paid_amount) > 0 ? formatCurrency(order.paid_amount) : '-'}
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-right">
                                                                {rcs && rcs.length > 0 ? (
                                                                    <div className="flex flex-col items-end gap-1">
                                                                        {rcs.map((rc, idx) => (
                                                                            <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 border border-green-200" title={`Método: ${rc.payment_method || 'N/A'}`}>
                                                                                {rc.name || 'RC'} {rc.value ? `(${formatCurrency(rc.value)})` : ''}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : <span className="text-gray-300 text-[10px]">-</span>}
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-right font-bold text-gray-900">
                                                                {Number(order.balance_due) > 0 ? (
                                                                    <span className="text-red-600 font-bold">{formatCurrency(order.balance_due)}</span>
                                                                ) : (
                                                                    <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-[10px]">PAGADO</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 align-top text-center">
                                                                {Number(order.balance_due) > 0 && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedOrder({
                                                                                ...order,
                                                                                total_amount: order.total_value,
                                                                                internal_orders_total: order.internal_invoice_value || 0
                                                                            });
                                                                            setShowPaymentModal(true);
                                                                        }}
                                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-900 border border-emerald-200 transition-all cursor-pointer"
                                                                        title="Aplicar Pago"
                                                                    >
                                                                        <DollarSign className="h-4 w-4" />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}

                                                {/* Filas de Paradas Adicionales */}
                                                {group.additionalStops && group.additionalStops.map((stop) => (
                                                    <tr key={`stop-${stop.id}`} className="bg-purple-50/50 hover:bg-purple-50">
                                                        <td className="px-4 py-2 align-top text-center">
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider bg-purple-100 text-purple-800">
                                                                PARADA
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 align-top text-gray-400 text-[10px]">-</td>
                                                        <td className="px-4 py-2 align-top">
                                                            <div className="flex items-start gap-2">
                                                                <MapPin className="h-3 w-3 text-purple-600 mt-0.5" />
                                                                <div>
                                                                    <div className="font-semibold text-purple-900 text-[10px] uppercase tracking-wide">
                                                                        {stop.stop_type === 'pickup' ? 'Recogida' :
                                                                            stop.stop_type === 'delivery' ? 'Entrega' :
                                                                                stop.stop_type === 'errand' ? 'Diligencia' :
                                                                                    'Parada Adicional'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700">{stop.address}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 align-top text-gray-500 text-[10px] italic">
                                                            {stop.description || '-'}
                                                        </td>
                                                        <td className="px-4 py-2 align-top text-right font-medium text-gray-400">
                                                            {formatCurrency(stop.payment_amount)}
                                                        </td>
                                                        <td className="px-4 py-2 align-top text-right text-gray-400">-</td>
                                                        <td className="px-4 py-2 align-top text-right text-gray-400">-</td>
                                                        <td className="px-4 py-2 align-top text-right font-bold text-purple-900">
                                                            {formatCurrency(stop.payment_amount)}
                                                        </td>
                                                        <td className="px-4 py-2 align-top text-center text-gray-400">-</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal de Aplicar Pago */}
            <PickupPaymentModal
                isOpen={showPaymentModal}
                order={selectedOrder}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedOrder(null);
                }}
                onConfirm={confirmPickupPayment}
            />
        </div>
    );
};

export default RoutePage;
